/**
 * StaticExpenseForm.jsx — Create/edit recurring (static) expense
 *
 * Fields: description, amount, frequency, method, category, chargeDate, expiresDate, notes
 * V1 parity: same field set as seOpenEntry() modal
 */

import { useState, useEffect } from 'react';
import { useCreateStaticExpense, useUpdateStaticExpense } from '../../hooks/useExpenses';
import { useToast } from '../layout/Toast';

const FREQUENCIES = ['monthly', 'annual', 'quarterly', 'weekly', 'one-time'];
const METHODS = ['Credit Card', 'ACH/Bank Transfer', 'Venmo', 'Zelle', 'Check', 'Cash', 'Auto-Pay', 'PayPal', 'Wire', 'Other'];
const CATEGORIES = ['Software', 'Insurance', 'Rent', 'Subscriptions', 'Equipment Lease', 'Utilities', 'Marketing', 'Legal', 'Accounting', 'Hosting', 'Storage'];

export default function StaticExpenseForm({ initialExpense = null, onSave, onCancel }) {
  const { toast } = useToast();
  const createMutation = useCreateStaticExpense();
  const updateMutation = useUpdateStaticExpense();

  const [form, setForm] = useState({
    description: '',
    amount: '',
    frequency: 'monthly',
    method: '',
    category: '',
    customCategory: '',
    chargeDate: '',
    expiresDate: '',
    notes: '',
  });

  useEffect(() => {
    if (initialExpense) {
      const knownCat = CATEGORIES.includes(initialExpense.category);
      const isFullDate = !['weekly', 'one-time'].includes(initialExpense.frequency || 'monthly');
      // For monthly/quarterly/annual, extract day from full date if needed
      let chargeDate = initialExpense.chargeDate || '';
      if (isFullDate && chargeDate.length > 2) {
        chargeDate = String(new Date(chargeDate + 'T12:00').getDate());
      }
      setForm({
        description: initialExpense.description || '',
        amount: initialExpense.amount || '',
        frequency: initialExpense.frequency || 'monthly',
        method: initialExpense.method || '',
        category: knownCat ? initialExpense.category : (initialExpense.category ? '__custom__' : ''),
        customCategory: knownCat ? '' : (initialExpense.category || ''),
        chargeDate,
        expiresDate: initialExpense.expiresDate || '',
        notes: initialExpense.notes || '',
      });
    }
  }, [initialExpense]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast('Description is required', 'error'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast('Valid amount is required', 'error'); return; }

    const category = form.category === '__custom__' ? form.customCategory.trim() : form.category;

    const data = {
      description: form.description.trim(),
      amount: parseFloat(form.amount),
      frequency: form.frequency,
      method: form.method || null,
      category: category || null,
      chargeDate: form.chargeDate || null,
      expiresDate: form.expiresDate || null,
      notes: form.notes.trim() || null,
    };

    try {
      if (initialExpense?.id) {
        await updateMutation.mutateAsync({ ...data, id: initialExpense.id });
        toast('Recurring expense updated');
      } else {
        await createMutation.mutateAsync(data);
        toast('Recurring expense created');
      }
      onSave?.();
    } catch (err) {
      console.error(err);
      toast(`Failed to save: ${err.message}`, 'error');
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const needsFullDate = form.frequency === 'weekly' || form.frequency === 'one-time';

  const inputCls = 'w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent transition-colors';
  const labelCls = 'block text-xs font-semibold text-text-dim mb-1.5';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Description */}
        <div>
          <label className={labelCls}>Description <span className="text-red-accent">*</span></label>
          <input type="text" value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="e.g. Adobe Creative Cloud" className={inputCls} />
        </div>
        {/* Amount */}
        <div>
          <label className={labelCls}>Amount <span className="text-red-accent">*</span></label>
          <input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="59.99" className={inputCls} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Frequency */}
        <div>
          <label className={labelCls}>Frequency</label>
          <select value={form.frequency} onChange={(e) => { set('frequency', e.target.value); set('chargeDate', ''); }} className={inputCls}>
            {FREQUENCIES.map((f) => <option key={f} value={f}>{f.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>)}
          </select>
        </div>
        {/* Method */}
        <div>
          <label className={labelCls}>Payment Method</label>
          <select value={form.method} onChange={(e) => set('method', e.target.value)} className={inputCls}>
            <option value="">—</option>
            {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        {/* Category */}
        <div>
          <label className={labelCls}>Category</label>
          <select value={form.category} onChange={(e) => set('category', e.target.value)} className={inputCls}>
            <option value="">—</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            <option value="__custom__">Other (custom)</option>
          </select>
          {form.category === '__custom__' && (
            <input type="text" value={form.customCategory} onChange={(e) => set('customCategory', e.target.value)} placeholder="Custom category" className={`${inputCls} mt-1`} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Charge Date — day-of-month for recurring, full date for weekly/one-time */}
        <div>
          <label className={labelCls}>{needsFullDate ? 'Charge Date' : 'Day of Month'}</label>
          {needsFullDate ? (
            <input type="date" value={form.chargeDate} onChange={(e) => set('chargeDate', e.target.value)} className={inputCls} />
          ) : (
            <select value={form.chargeDate} onChange={(e) => set('chargeDate', e.target.value)} className={inputCls}>
              <option value="">—</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={String(d)}>{d}{d === 31 || d === 30 || d === 29 ? ` (clamped in short months)` : ''}</option>
              ))}
            </select>
          )}
        </div>
        {/* Expires Date */}
        <div>
          <label className={labelCls}>Expires Date</label>
          <input type="date" value={form.expiresDate} onChange={(e) => set('expiresDate', e.target.value)} className={inputCls} />
        </div>
        {/* Notes */}
        <div>
          <label className={labelCls}>Notes</label>
          <input type="text" value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Account #, auto-renew, etc." className={inputCls} />
        </div>
      </div>

      <div className="flex items-center gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-xs text-text-dim hover:text-text-primary transition-colors">Cancel</button>
        <button type="submit" disabled={isLoading} className="px-4 py-2 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50">
          {isLoading ? 'Saving...' : initialExpense ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}
